import { Fragment, useState, FormEvent, KeyboardEvent } from 'react';
import {
  FileText,
  GraduationCap,
  BookOpen,
  Briefcase,
  BarChart3,
  Star,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Sparkles,
  Send,
  Image as ImageIcon,
  ChevronRight,
  Loader,
  Check,
  LucideIcon,
} from 'lucide-react';
import { usePrototype2 } from '../Prototype2Context';
import { ParagraphMeta, SectionMeta } from '../prototype2Data';
import DraftBody from '../../_shared/DraftBody';

const SECTION_ICONS: Record<string, LucideIcon> = {
  'college-eligibility': GraduationCap,
  'college-readiness': BookOpen,
  'cte-pathways': Briefcase,
  'equity-analysis': BarChart3,
  'gibson-spotlight': Star,
  recommendations: Lightbulb,
  'executive-summary': FileText,
  'postsecondary-readiness-overview': BarChart3,
  'equity-analysis-progress-persistent-gaps': BarChart3,
  'college-readiness-deep-dive': BookOpen,
  'cte-pathways-career-readiness': Briefcase,
  'spotlight-gibson-ek-school': Star,
  'recommendations-next-steps': Lightbulb,
};

const resolveSectionIcon = (section: SectionMeta): LucideIcon => {
  if (SECTION_ICONS[section.id]) return SECTION_ICONS[section.id];

  const title = section.title.toLowerCase();
  if (title.includes('executive') || title.includes('summary')) return FileText;
  if (title.includes('equity') || title.includes('gap')) return BarChart3;
  if (title.includes('college') || title.includes('readiness') || title.includes('eligibility')) return BookOpen;
  if (title.includes('cte') || title.includes('career')) return Briefcase;
  if (title.includes('gibson') || title.includes('spotlight')) return Star;
  if (title.includes('recommend')) return Lightbulb;
  if (title.includes('postsecondary') || title.includes('graduation')) return GraduationCap;

  return FileText;
};

interface CollapsedCardProps {
  paragraph: ParagraphMeta;
  onClick: () => void;
}

const CollapsedCard = ({ paragraph, onClick }: CollapsedCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center justify-between w-full px-7 py-5 rounded-lg bg-[#fafafa] border border-[#e5e5e5] shadow-[0_1px_1.75px_rgba(0,0,0,0.05)] hover:shadow-md transition-shadow"
  >
    <span className="text-[13px] font-semibold text-[#737373] uppercase tracking-wide">Paragraph {paragraph.num}</span>
    <ChevronDown className="w-4 h-4 text-[#737373]" />
  </button>
);

interface ExpandedCardProps {
  section: SectionMeta;
  paragraph: ParagraphMeta;
}

const ParagraphDraftingSkeleton = () => (
  <div className="flex flex-col gap-2 py-1">
    <div className="h-3 rounded bg-[#f5f5f5] w-full" />
    <div className="h-3 rounded bg-[#f5f5f5] w-[92%]" />
    <div className="h-3 rounded bg-[#f5f5f5] w-[85%]" />
    <div className="h-3 rounded bg-[#f5f5f5] w-[70%]" />
  </div>
);

const ExpandedCard = ({ section, paragraph }: ExpandedCardProps) => {
  const {
    drafts,
    paragraphChatHistories,
    isChatPending,
    pendingParagraphId,
    updatedParagraphId,
    sendChat,
    toggleParagraphExpanded,
  } = usePrototype2();
  const [input, setInput] = useState('');
  const draft = drafts[paragraph.id] ?? '';
  const hasDraft = draft.trim().length > 0;
  const isParagraphPending = pendingParagraphId === paragraph.id;
  const isParagraphUpdated = updatedParagraphId === paragraph.id;
  const paragraphChat = paragraphChatHistories[paragraph.id] ?? [];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isChatPending) return;
    const message = input;
    setInput('');
    await sendChat(message, {
      intent: 'edit-current-draft',
      active_section_id: paragraph.id,
      current_draft: draft,
      ui_state: { panel: 'chat-open', last_user_action: 'modify-paragraph' },
      response_hint: { shape: 'rewrite-paragraph' },
    });
  };

  const handleDraftEmpty = async () => {
    if (isChatPending) return;
    await sendChat(`Please draft "${paragraph.title}" for the ${section.title} section.`, {
      intent: 'draft',
      active_section_id: paragraph.id,
      current_draft: '',
      ui_state: { panel: 'chat-open', last_user_action: 'draft-empty-paragraph' },
      response_hint: { shape: 'draft-paragraph' },
    });
  };

  return (
    <div className="rounded-lg bg-[#fafafa] border border-[#e5e5e5] shadow-[0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden">
      <div className="flex flex-col gap-4 px-7 py-6">
        <button
          type="button"
          onClick={() => toggleParagraphExpanded(paragraph.id)}
          className="flex items-center justify-between"
        >
          <span className="text-[12px] font-semibold text-[#737373] uppercase tracking-wide">
            Paragraph {paragraph.num}
          </span>
          <ChevronUp className="w-5 h-5 text-[#737373]" />
        </button>
        <h3 className="text-[18px] font-semibold text-[#0a0a0a] text-left">{paragraph.title}</h3>
        {isParagraphPending && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#3b82f6]/10 border border-[#3b82f6]/20">
            <Loader className="w-3.5 h-3.5 text-[#3b82f6] animate-spin shrink-0" />
            <span className="text-[13px] font-medium text-[#3b82f6]">
              {hasDraft ? 'Revising paragraph…' : 'Drafting paragraph…'}
            </span>
          </div>
        )}
        {isParagraphUpdated && !isParagraphPending && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#16a34a]/10 border border-[#16a34a]/20">
            <Check className="w-3.5 h-3.5 text-[#16a34a] shrink-0" />
            <span className="text-[13px] font-medium text-[#16a34a]">Paragraph updated</span>
          </div>
        )}
        {isParagraphPending && !hasDraft ? (
          <ParagraphDraftingSkeleton />
        ) : hasDraft ? (
          <div className={isParagraphPending ? 'opacity-60 pointer-events-none' : undefined}>
            <DraftBody text={draft} />
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-[#a3a3a3] italic">No content yet.</p>
            <button
              type="button"
              onClick={handleDraftEmpty}
              disabled={isChatPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-white bg-[#171717] hover:bg-[#2a2a2a] rounded-md transition-colors disabled:opacity-40"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Draft this paragraph
            </button>
          </div>
        )}
        {paragraph.hasChart && (
          <div className="w-full h-[180px] rounded bg-[#f5f5f5] flex flex-col items-center justify-center gap-1">
            <ImageIcon className="w-5 h-5 text-[#a3a3a3]" />
            <span className="text-[13px] text-[#737373]">Chart / visualization</span>
          </div>
        )}
      </div>

      <hr className="border-[#e5e5e5]" />

      <div className="flex flex-col gap-3 px-7 py-4 bg-[#f5f5f5]">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-[#737373]" />
          <span className="text-[13px] font-semibold text-[#737373]">Modify this section</span>
        </div>
        <div className="flex flex-col gap-3">
          {paragraphChat.length === 0 && (
            <div className="flex items-start gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#171717] shrink-0 mt-0.5">
                <Sparkles className="w-3 h-3 text-white" />
              </div>
              <div className="rounded-lg bg-[#f5f5f5] border border-[#e5e5e5] px-3.5 py-2.5 flex-1">
                <p className="text-[13px] text-[#0a0a0a] leading-[1.5]">
                  I can revise the tone, expand on equity gaps, add a source citation, or adjust the data emphasis. What
                  would you like to change?
                </p>
              </div>
            </div>
          )}
          {paragraphChat.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'items-start gap-2'}`}>
              {m.role === 'assistant' && (
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#171717] shrink-0 mt-0.5">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
              )}
              <div
                className={`rounded-lg px-3.5 py-2.5 ${
                  m.role === 'user'
                    ? 'bg-[#171717] text-white max-w-[85%]'
                    : 'bg-[#f5f5f5] border border-[#e5e5e5] text-[#0a0a0a] flex-1'
                }`}
              >
                {m.role === 'user' ? (
                  <p className="text-[13px] leading-[1.5]">{m.content}</p>
                ) : (
                  <DraftBody text={m.content} />
                )}
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              isParagraphPending
                ? hasDraft
                  ? 'Revising paragraph…'
                  : 'Drafting paragraph…'
                : 'Ask to modify this section...'
            }
            disabled={isChatPending}
            className="flex-1 h-10 px-3 rounded-lg bg-[#fafafa] text-[13px] text-[#0a0a0a] outline-none border border-[#e5e5e5] focus:border-[#0a0a0a] transition-colors disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={isChatPending || !input.trim()}
            className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#171717] shrink-0 hover:bg-[#2a2a2a] transition-colors disabled:opacity-40"
          >
            {isParagraphPending ? (
              <Loader className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Send className="w-4 h-4 text-white" />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

interface SectionBlockProps {
  section: SectionMeta;
  isFirst: boolean;
}

const SectionBlock = ({ section, isFirst }: SectionBlockProps) => {
  const { expandedParagraphIds, toggleParagraphExpanded } = usePrototype2();

  return (
    <Fragment>
      <div className="flex flex-col gap-4 px-7 py-6 rounded-lg bg-[#fafafa] border border-[#e5e5e5] shadow-[0_1px_1.75px_rgba(0,0,0,0.05)]">
        <span className="text-[12px] font-semibold text-[#737373] uppercase tracking-wide">
          {isFirst ? 'Introduction' : 'Section'}
        </span>
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-[#0a0a0a]">{section.title}</span>
          <ChevronDown className="w-5 h-5 text-[#737373]" />
        </div>
        <p className="text-sm text-[#0a0a0a] leading-[1.7]">{section.intro}</p>
      </div>

      <div className="flex flex-col gap-6 pl-6">
        {section.paragraphs.map((p) =>
          expandedParagraphIds.has(p.id) ? (
            <ExpandedCard key={p.id} section={section} paragraph={p} />
          ) : (
            <CollapsedCard key={p.id} paragraph={p} onClick={() => toggleParagraphExpanded(p.id)} />
          )
        )}
      </div>
    </Fragment>
  );
};

const Screen03DraftReport = () => {
  const { sectionStatuses, reportSections } = usePrototype2();
  const [activeSectionId, setActiveSectionId] = useState<string>(() => reportSections[0]?.id ?? '');

  const activeSection = reportSections.find((s) => s.id === activeSectionId) ?? reportSections[0];

  const handleSidebarKeyDown = (e: KeyboardEvent<HTMLButtonElement>, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActiveSectionId(id);
    }
  };

  if (!reportSections.length || !activeSection) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#fafafa] font-['Inter']">
        <p className="text-sm text-[#737373]">Approve an outline in step 2 to begin drafting.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full bg-[#fafafa] font-['Inter']">
      <div className="flex flex-col flex-1 min-w-0">
        <header className="flex items-center gap-3 px-6 h-14 border-b border-[#e5e5e5] bg-[#fafafa] shrink-0">
          <FileText className="w-5 h-5 text-[#0a0a0a]" />
          <span className="text-[15px] font-semibold text-[#0a0a0a]">EOAA Report Builder</span>
          <span className="text-sm text-[#737373]">|</span>
          <span className="text-sm text-[#737373]">Issaquah School District - 2023-24</span>
          <div className="flex-1" />
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-[#f5f5f5] text-[#737373] border border-[#e5e5e5]">
            Step 3 of 3: Draft Report
          </span>
        </header>

        <div className="flex flex-1 min-h-0">
          <div className="flex flex-col gap-1 w-[260px] shrink-0 px-2 py-4 bg-[#fafafa] border-r border-[#e4e4e7] overflow-y-auto">
            <div className="px-2 py-2">
              <span className="text-[11px] font-semibold text-[#737373] tracking-widest uppercase">
                Report Sections
              </span>
            </div>
            {reportSections.map((s) => {
              const Icon = resolveSectionIcon(s);
              const isActive = s.id === activeSectionId;
              const status = sectionStatuses[s.id] ?? s.status;
              return (
                <Fragment key={s.id}>
                  <button
                    type="button"
                    onClick={() => setActiveSectionId(s.id)}
                    onKeyDown={(e) => handleSidebarKeyDown(e, s.id)}
                    className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-md ${
                      isActive ? 'bg-[#f5f5f5]' : 'hover:bg-[#f5f5f5]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${isActive ? 'text-[#0a0a0a]' : 'text-[#737373]'}`} />
                      <span className={`text-sm ${isActive ? 'font-medium text-[#0a0a0a]' : 'text-[#737373]'}`}>
                        {s.title}
                      </span>
                    </div>
                    {status === 'complete' && <ChevronRight className="w-3.5 h-3.5 text-[#16a34a]" />}
                  </button>
                  {isActive &&
                    s.paragraphs.map((p) => (
                      <div key={p.id} className="px-2 py-1 ml-8 rounded text-[13px] text-[#737373] leading-[1.4]">
                        {p.title}
                      </div>
                    ))}
                </Fragment>
              );
            })}
          </div>

          <div className="flex flex-col flex-1 min-w-0 bg-[#f5f5f5] overflow-y-auto px-12 py-8 gap-6">
            <SectionBlock section={activeSection} isFirst />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Screen03DraftReport;
